import React, { ReactNode } from "react";
import Button from "../../reusable/Button";
import {
  usePreturi,
  pricesForService,
  promoLabelFor,
  baseDisplay,
  promoDisplay,
  hasPromo,
  PriceItem,
} from "../../../utils/preturi";
import "./index.style.css";

interface Props {
  price?: string | ReactNode;
  price1?: string | ReactNode;
  price2?: string | ReactNode;
  price3?: string | ReactNode;
  price4?: string | ReactNode;
  price5?: string | ReactNode;
  price6?: string | ReactNode;
  price7?: string | ReactNode;
  price8?: string | ReactNode;
  price9?: string | ReactNode;
  price10?: string | ReactNode;
  promoPrice?: string;
  promoPrice1?: string;
  promoPrice2?: string;
  promoPrice3?: string;
  promoPrice4?: string;
  promoPrice5?: string;
  promoPrice6?: string;
  promoPrice7?: string;
  promoPrice8?: string;
  promoPrice9?: string;
  promoPrice10?: string;
  title: string | ReactNode;
  time?: string;
  description: string;
  Src: string;
  category?: string;
  customPrice?: string;
  id?: string;
  customPrice1?: string;
}

// Services that should only scroll to form without auto-selecting
const scrollOnlyServices = [
  "Nos produits",
  // "Massage thérapeutique",
  "Raffermissement Corporel par HIFU",
  "Lifting HIFU",
  // Add more services here as needed
];

function scrollToFormAndHighlight(service: string) {
  const form = document.getElementById("form");
  if (form) {
    form.scrollIntoView({ behavior: "smooth" });

    // Only highlight and select service if it's not in the scroll-only list
    if (!scrollOnlyServices.includes(service)) {
      window.dispatchEvent(
        new CustomEvent("highlightService", { detail: { service } })
      );
    }
  }
}

// Helper to map CardItem/CardItemReverse title prop to the correct string for the Form (payload/backend).
// Exported so Category can use it when pre-filling the form — ensures only canonical name is sent, never display labels like "-25% en mars".
export const getServiceName = (title: Props["title"]): string => {
  // Extract text content from title (handles both string and JSX)
  const extractText = (title: Props["title"]): string => {
    if (typeof title === "string") return title;
    if (!title) return "";
    if (Array.isArray(title)) return title.map(extractText).join(" ");
    if (typeof title === "object" && "props" in title) {
      return extractText(title.props.children);
    }
    return String(title);
  };

  const titleText = extractText(title).trim();

  // Comprehensive mapping table
  const serviceMapping: { [key: string]: string } = {
    // Exact matches
    "Lifting HIFU": "Lifting HIFU",
    "RADIANCE - Traitement à l'ADN de saumon":
      "RADIANCE - Traitement à l'ADN de saumon",
    "Peeling acide": "Peeling acide",
    "Peeling au Laser": "Peeling Laser",
    Mésothérapie: "Mésothérapie",
    Cryothérapie: "Cryothérapie",
    "Remodelage Corporel": "Remodelage Corporel",
    "Raffermissement Corporel par HIFU": "Raffermissement Corporel par HIFU",
    // "Massage thérapeutique": "Massage thérapeutique",

    // JSX title mappings for Hydrafacial services
    "-ESSENTIEL-\nSoin du Visage": "ESSENTIEL - Soin du Visage",
    "ESSENTIEL - Soin du Visage": "ESSENTIEL - Soin du Visage",
    "ESSENTIEL Soin du Visage": "ESSENTIEL - Soin du Visage",
    "-GOLD-\nSoin du Visage avec Hydrafacial Diamant":
      "GOLD - Soin du Visage par Hydrafacial Diamant",
    "GOLD - Soin du Visage avec Hydrafacial Diamant":
      "GOLD - Soin du Visage par Hydrafacial Diamant",
    "GOLD Soin du Visage avec Hydrafacial Diamant":
      "GOLD - Soin du Visage par Hydrafacial Diamant",
    "-DIAMOND-\nSoin du Visage avec Hydrafacial Diamant":
      "DIAMOND - Soin du Visage par Hydrafacial Diamant",
    "DIAMOND - Soin du Visage avec Hydrafacial Diamant":
      "DIAMOND - Soin du Visage par Hydrafacial Diamant",
    "DIAMOND Soin du Visage avec Hydrafacial Diamant":
      "DIAMOND - Soin du Visage par Hydrafacial Diamant",
    "-PLATINUM-\nSoin du Visage avec Hydrafacial Diamant":
      "PLATINUM - Soin du Visage par Hydrafacial Diamant",
    "PLATINUM - Soin du Visage avec Hydrafacial Diamant":
      "PLATINUM - Soin du Visage par Hydrafacial Diamant",
    "PLATINUM Soin du Visage avec Hydrafacial Diamant":
      "PLATINUM - Soin du Visage par Hydrafacial Diamant",

    // Depigmentation services
    "Dépigmentation des sourcils et des tatouages au Pico-Laser":
      "Dépigmentation des sourcils et tatouages avec Pico-Laser",
    "Dépigmentation des taches avec Pico-Laser":
      "Dépigmentation des taches avec Pico-Laser",

    // Epilation services
    "Épilation définitive": "Épilation définitive",
    "Épilation à la cire": "Épilation à la cire",

    // Other specific services
    "Élimination au laser des varices fines et de la couperose":
      "Élimination au laser des varices fines et de la cupérose",

    // Partial matches for fallback
    GOLD: "GOLD - Soin du Visage par Hydrafacial Diamant",
    DIAMOND: "DIAMOND - Soin du Visage par Hydrafacial Diamant",
    PLATINUM: "PLATINUM - Soin du Visage par Hydrafacial Diamant",
    ESSENTIEL: "ESSENTIEL - Soin du Visage",
    Dépigmentation: "Dépigmentation des sourcils et tatouages avec Pico-Laser",
    Épilation: "Épilation définitive",
    Remodelage: "Remodelage Corporel",
    Raffermissement: "Raffermissement Corporel par HIFU",
    // Massage: "Massage thérapeutique",
  };

  // Try exact match first
  if (serviceMapping[titleText]) {
    return serviceMapping[titleText];
  }

  // Try partial matches
  for (const [key, value] of Object.entries(serviceMapping)) {
    if (
      titleText.toLowerCase().includes(key.toLowerCase()) ||
      key.toLowerCase().includes(titleText.toLowerCase())
    ) {
      return value;
    }
  }

  // Fallback: return the original text if no match found
  console.warn(`No service mapping found for: "${titleText}"`);
  return titleText;
};

// Randează liniile de preț ale unui serviciu din colecția Preturi (gestionate
// din admin). Prețul de bază apare tăiat, prețul redus în roșu, când reducerea
// e activă. Un spațiu separă sub-grupurile (ex. înainte de fiecare serviciu nou
// care urmează după o linie de tip „Pack").
const PriceLines: React.FC<{
  items: PriceItem[];
  promoLabel?: string;
}> = ({ items, promoLabel }) => {
  return (
    <>
      <p className="service-price">Prix Promotionnel</p>
      {promoLabel && (
        <p className="service-price promo-price">{promoLabel}</p>
      )}
      <br />
      {items.map((item, i) => {
        const newGroup =
          i > 0 &&
          !/pack/i.test(item.label) &&
          /pack/i.test(items[i - 1].label);
        const promo = hasPromo(item);
        return (
          <React.Fragment key={item._id || i}>
            {newGroup && <br />}
            <p className="service-price">
              {item.label ? item.label + " - " : ""}
              {promo ? (
                <>
                  <del>{baseDisplay(item)}</del>{" "}
                  <span className="promo-price">{promoDisplay(item)}</span>
                </>
              ) : (
                baseDisplay(item)
              )}
              {item.note ? " " + item.note : ""}
            </p>
          </React.Fragment>
        );
      })}
    </>
  );
};

const CardItem: React.FC<Props> = ({
  price,
  price1,
  price2,
  price3,
  price4,
  price5,
  price6,
  price7,
  price8,
  price9,
  price10,
  promoPrice,
  promoPrice1,
  promoPrice2,
  promoPrice3,
  promoPrice4,
  promoPrice5,
  promoPrice6,
  promoPrice7,
  promoPrice8,
  promoPrice9,
  promoPrice10,
  time,
  title,
  description,
  Src,
  category = "services",
  customPrice,
  id,
}) => {
  const { data: preturiData } = usePreturi();
  const dbItems = pricesForService(preturiData, id || "");
  const promoLabel = promoLabelFor(preturiData, id || "");
  return (
    <section id={id || category} className="services">
      <div className="services-main">
        <img
          className="hidden-desktop"
          src={Src}
          alt={`${getServiceName(title)} – Centre Esthétique Basilix, Bruxelles`}
          loading="lazy"
          decoding="async"
        />
        <div className="services-all-text">
          {typeof title === "string" ? <h2>{title}</h2> : title}
          <p dangerouslySetInnerHTML={{ __html: description }} />
          {dbItems.length > 0 ? (
            <PriceLines items={dbItems} promoLabel={promoLabel} />
          ) : (
            <>
              {customPrice === "custom" && (
                <p className="service-price">
                  <span className="promo-price"> Offre Juillet / Août </span>
                  <br></br> <br></br> 1ère séance - <del>300 €</del>
                  <span className="promo-price"> 210 € </span>
                </p>
              )}
              {customPrice === "custom" && (
                <p className="service-price">
                  2ème séance - <del>280 €</del>
                  <span className="promo-price"> 196 € </span>
                </p>
              )}
              {customPrice === "custom" && (
                <p className="service-price">
                  3ème séance - <del>260 €</del>
                  <span className="promo-price"> 182 € </span>
                </p>
              )}
              {customPrice === "radiance" && (
                <>
                  <p className="service-price">Prix Promotionnel</p>
                  <p className="service-price promo-price">Offre septembre</p>
                  <br />
                  <p className="service-price">
                    Visage - <del>180 €</del>{" "}
                    <span className="promo-price">135 €</span>
                  </p>
                  <p className="service-price">
                    ( Pack 4 séances - <del>720 €</del> 640 € ){" "}
                    <span className="promo-price">480 €</span>
                  </p>
                  <br />
                  <p className="service-price">
                    Cou - <del>150 €</del>{" "}
                    <span className="promo-price">112,50 €</span>
                  </p>
                  <p className="service-price">
                    ( Pack 4 séances - <del>600 €</del> 540 € ){" "}
                    <span className="promo-price">405 €</span>
                  </p>
                  <br />
                  <p className="service-price">
                    Décolleté - <del>220 €</del>{" "}
                    <span className="promo-price">165 €</span>
                  </p>
                  <p className="service-price">
                    ( Pack 4 séances - <del>880 €</del> 800 € ){" "}
                    <span className="promo-price">600 €</span>
                  </p>
                </>
              )}
          {price && promoPrice && (
            <p className="service-price">
              Prix Promotionnel{" "}
              {price1 && (
                <>
                  <br />
                  <br />
                </>
              )}
              <del>{price}</del> € {time} <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice} €</span>
            </p>
          )}
          {price && !promoPrice && (
            <p className="service-price">
              Prix Promotionnel{" "}
              {price1 && (
                <>
                  <br />
                  <br />
                </>
              )}
              {price} € {time}
            </p>
          )}
          {price1 && !promoPrice1 && <p className="service-price">{price1} €</p>}
          {price2 && !promoPrice2 && <p className="service-price"> {price2} €</p>}
          {price3 && !promoPrice3 && <p className="service-price"> {price3} €</p>}
          {price4 && !promoPrice4 && <p className="service-price"> {price4} €</p>}
          {price5 && !promoPrice5 && <p className="service-price"> {price5} €</p>}
          {price6 && !promoPrice6 && <p className="service-price"> {price6} €</p>}
          {price7 && !promoPrice7 && <p className="service-price"> {price7} €</p>}
          {price8 && !promoPrice8 && <p className="service-price"> {price8} €</p>}
          {price9 && !promoPrice9 && <p className="service-price"> {price9} €</p>}
          {price10 && !promoPrice10 && <p className="service-price"> {price10} €</p>}
          {price1 && promoPrice1 && (
            <p className="service-price">
              <del>{price1}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice1} €</span>
            </p>
          )}
          {price2 && promoPrice2 && (
            <p className="service-price">
              <del>{price2}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice2} €</span>
            </p>
          )}{" "}
          {price3 && promoPrice3 && (
            <p className="service-price">
              <del>{price3}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice3} €</span>
            </p>
          )}{" "}
          {price4 && promoPrice4 && (
            <p className="service-price">
              <del>{price4}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice4} €</span>
            </p>
          )}{" "}
          {price5 && promoPrice5 && (
            <p className="service-price">
              <del>{price5}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice5} €</span>
            </p>
          )}{" "}
          {price6 && promoPrice6 && (
            <p className="service-price">
              <del>{price6}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice6} €</span>
            </p>
          )}
            </>
          )}
          <Button
            title="Prendre rendez-vous"
            onClick={() => scrollToFormAndHighlight(getServiceName(title))}
            dataService={getServiceName(title)}
          />
        </div>
        <img
          className="hidden-mobile"
          src={Src}
          alt={`${getServiceName(title)} – Centre Esthétique Basilix, Bruxelles`}
          loading="lazy"
          decoding="async"
        />
      </div>
    </section>
  );
};

const CardItemReverse: React.FC<Props> = ({
  price,
  price1,
  price2,
  price3,
  price4,
  price5,
  price6,
  price7,
  price8,
  price9,
  price10,
  promoPrice,
  promoPrice1,
  promoPrice2,
  promoPrice3,
  promoPrice4,
  promoPrice5,
  promoPrice6,
  promoPrice7,
  promoPrice8,
  promoPrice9,
  promoPrice10,
  time,
  title,
  description,
  Src,
  category = "services",
  id,
  customPrice,
  customPrice1,
}) => {
  const { data: preturiData } = usePreturi();
  const dbItems = pricesForService(preturiData, id || "");
  const promoLabel = promoLabelFor(preturiData, id || "");
  return (
    <section id={id || category} className="services">
      <div className="services-main">
        <img
          src={Src}
          alt={`${getServiceName(title)} – Centre Esthétique Basilix, Bruxelles`}
          loading="lazy"
          decoding="async"
        />
        <div className="services-all-text">
          {typeof title === "string" ? <h2>{title}</h2> : title}
          <p dangerouslySetInnerHTML={{ __html: description }} />
          {dbItems.length > 0 ? (
            <PriceLines items={dbItems} promoLabel={promoLabel} />
          ) : (
            <>
          {price && promoPrice && (
            <p className="service-price">
              Prix Promotionnel{" "}
              {price1 && (
                <>
                  <br />
                  <br />
                </>
              )}
              <del>{price}</del> € {time} <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice} €</span>
            </p>
          )}
          {price && !promoPrice && (
            <p className="service-price">
              Prix Promotionnel{" "}
              {price1 && (
                <>
                  <br />
                  <br />
                </>
              )}
              {price} € {time}
            </p>
          )}
          {price1 && !promoPrice1 && <p className="service-price">{price1} €</p>}
          {price2 && !promoPrice2 && <p className="service-price"> {price2} €</p>}
          {price3 && !promoPrice3 && <p className="service-price"> {price3} €</p>}
          {price4 && !promoPrice4 && <p className="service-price"> {price4} €</p>}
          {customPrice === "custom2" && (
            <p className="service-price">
              <b>Pack Visage</b> ( Front + Contour des yeux + Joues + Menton ) -{" "}
              <del>640 €</del>
              <span className="promo-price-pack">
                <b> 550 €</b>
              </span>
            </p>
          )}
          <br />
          {price5 && !promoPrice5 && <p className="service-price"> {price5} €</p>}
          {price6 && !promoPrice6 && <p className="service-price"> {price6} €</p>}
          {price7 && !promoPrice7 && <p className="service-price"> {price7} €</p>}
          {customPrice1 === "custom2" && (
            <p className="service-price">
              <b>Pack Cou</b> ( Double menton + Cou + Décolleté ) -{" "}
              <del>680 €</del>
              <span className="promo-price-pack">
                {" "}
                <b>580 €</b>
              </span>
            </p>
          )}
          {price8 && !promoPrice8 && <p className="service-price"> {price8} €</p>}
          {price9 && !promoPrice9 && <p className="service-price"> {price9} €</p>}
          {price10 && !promoPrice10 && <p className="service-price"> {price10} €</p>}
          {price1 && promoPrice1 && (
            <p className="service-price">
              <del>{price1}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice1} €</span>
            </p>
          )}
          {price2 && promoPrice2 && (
            <p className="service-price">
              <del>{price2}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice2} €</span>
            </p>
          )}{" "}
          {price3 && promoPrice3 && (
            <p className="service-price">
              <del>{price3}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice3} €</span>
            </p>
          )}{" "}
          {price4 && promoPrice4 && (
            <p className="service-price">
              <del>{price4}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice4} €</span>
            </p>
          )}{" "}
          {price5 && promoPrice5 && (
            <p className="service-price">
              <del>{price5}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice5} €</span>
            </p>
          )}{" "}
          {price6 && promoPrice6 && (
            <p className="service-price">
              <del>{price6}</del> € <br /> <br />
              <span className="promo-price">Offre Juillet {promoPrice6} €</span>
            </p>
          )}
            </>
          )}
          <Button
            title="Prendre rendez-vous"
            onClick={() => scrollToFormAndHighlight(getServiceName(title))}
            dataService={getServiceName(title)}
          />
        </div>
      </div>
    </section>
  );
};

const Cart = () => {
  return (
    <section id="allServices">
      <div className="services-container">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill="#ffffff"
            fill-opacity="1"
            d="M0,32L80,32C160,32,320,32,480,80C640,128,800,224,960,234.7C1120,245,1280,171,1360,133.3L1440,96L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"
          ></path>
        </svg>
        <h2 className="services-title-logo">Esthétique Basilix</h2>
        <h2>Soins</h2>
        <a
          className="services-logo"
          href="#banner"
          aria-label="Retour à l'accueil – Centre Esthétique Basilix"
        />
      </div>

      <CardItemReverse
        id="lifting-hifu"
        price={"Front - 170"}
        price1={"Contour des yeux - 150"}
        price2={"Joues - 150"}
        price3={"Menton - 170"}
        customPrice={"custom2"}
        price5={"Double menton - 200"}
        price6={"Cou - 200"}
        price7={"Décolleté - 280"}
        customPrice1={"custom2"}
        title={"Lifting HIFU"}
        description={`Dans le domaine de l'esthétique non invasive, HIFU représente une innovation remarquable et une alternative aux procédures chirurgicales pour lutter contre les signes du <b>vieillissement</b>, tels que les <b>rides</b> et la perte d'<b>élasticité</b>. HIFU (High Intensity Focused Ultrasound) utilise des ultrasons focalisés pour chauffer les couches profondes de la peau, la renforçant et la tonifiant, réduisant ainsi les rides et les lignes fines. 
Le traitement offre ainsi un effet de <b>lifting visible </b> sans nécessiter d'interventions chirurgicales. Il est idéal pour les personnes souhaitant améliorer l'apparence de leur peau sans recourir à des méthodes complexes et risquées. Bien que les effets initiaux puissent être observés <b>immédiatement</b>, les résultats finaux deviennent évidents au cours de plusieurs mois, à mesure que le collagène se renouvelle. Ces résultats peuvent durer jusqu'à 18 mois.`}
        Src={"https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/2026-06-30-16-28-48-336.jpg?alt=media&token=4e2658a0-d949-4494-8ad7-fcfb3c341a39"}
      />

      <CardItem
        id="soin-rADiaNce"
        category="face"
        customPrice="radiance"
        // promoPrice="56"
        // time="(45min)"
        title={"- RADIANCE - Traitement à l'ADN de saumon"}
        description={`Le traitement à l'ADN de saumon est une procédure esthétique qui utilise des polynucléotides (fragments d'ADN) extraits du sperme de saumon pour <b>rajeunir</b> la peau, en particulier celle du <b>visage</b>, du <b>cou</b> et du <b>décolleté</b>. Ce traitement améliore la texture et la fermeté de la peau en stimulant la <b>régénération cellulaire</b>, la production de collagène et l'hydratation profonde. <br>

Les polynucléotides de l'ADN de saumon sont biocompatibles avec l'ADN humain et favorisent la régénération cellulaire depuis les couches les plus profondes de la peau, aidant celle-ci à produire plus de collagène et d'élastine. Cela conduit à une fermeté et à une <b>élasticité</b> accrues, améliore la barrière cutanée et offre une <b>hydratation profonde</b>, redonnant vitalité à la peau et aidant à la protéger des dommages causés par des facteurs externes et le stress oxydatif.<br>

Les principaux bénéfices sont le rajeunissement de la peau grâce à l'amélioration de sa texture, la réduction de l'apparition des lignes fines et des rides, et un <b>effet immédiat</b> "glowy" d'augmentation de la <b>luminosité du teint</b>.`}
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/acum1.jpg?alt=media&token=82c5f765-5778-4104-b20f-c82da08ff114"
        }
      />

      <CardItemReverse
        id="peeling-acide"
        category="face"
        price="130"
        // promoPrice="56"
        // time="(45min)"
        title={"Peeling acide"}
        description={`
    Le Peeling Acide, ou 𝗣𝗲𝗲𝗹𝗶𝗻𝗴 𝗰𝗵𝗶𝗺𝗶𝗾𝘂𝗲 𝗱𝗼𝘂𝘅, est une exfoliation réalisée avec des 𝗮𝗰𝗶𝗱𝗲𝘀 𝗻𝗮𝘁𝘂𝗿𝗲𝗹𝘀 qui éliminent délicatement la couche supérieure de la peau, 
    étant personnalisé en fonction de vos besoins spécifiques.

    Le traitement offre de nombreux bénéfices à votre peau et une <b>hydratation</b> 𝗶𝗻𝘁𝗲𝗻𝘀𝗲 qui laisse la peau douce et revitalisée dès la première séance. 
    L'amélioration de la 𝘁𝗲𝘅𝘁𝘂𝗿𝗲 de la peau et la réduction des 𝗶𝗺𝗽𝗲𝗿𝗳𝗲𝗰𝘁𝗶𝗼𝗻𝘀 telles que les 𝘁𝗮𝗰𝗵𝗲𝘀 𝗽𝗶𝗴𝗺𝗲𝗻𝘁𝗮𝗶𝗿𝗲𝘀 et les 𝗰𝗶𝗰𝗮𝘁𝗿𝗶𝗰𝗲𝘀 sont des résultats visibles 
    qui contribuent à un aspect plus uniforme et sain du visage, la peau devenant plus <b>éclatante</b> avec un aspect plus 𝗷𝗲𝘂𝗻𝗲.

   <br/> Contrairement au peeling chimique au 𝘱𝘩𝘦𝘯𝘰𝘭, après avoir effectué l'exfoliation chimique avec des acides naturels de fruits, 
    vous pouvez retourner à votre vie quotidienne normalement, en suivant les instructions du spécialiste.
  `}
        Src="https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/2025-03-03-14-04-28-274.jpg?alt=media&token=33e15556-84fe-4a91-8cfd-6066a9d8a9cf"
      />

      <CardItem
        id="peeling-laser"
        price={<span>Visage - 180€</span>}
        price1={<span>Visage + Cou - 220€</span>}
      
        title={"Peeling au Laser"}
        description={
          `Également connu sous le nom de <b>Hollywood Peeling</b>, le peeling laser au charbon actif est une procédure cosmétique non invasive qui consiste à appliquer une fine couche de <b>charbon actif</b> sur la peau, suivie d'une exposition à un <b>laser</b> dermatologique spécial Pico-Laser. Ce traitement présente plusieurs avantages, notamment la <b>fermeture des pores</b> dilatés, l’<b>exfoliation</b> de la couche supérieure de la peau et la stimulation de la production de collagène, laissant la peau plus tonique et rajeunie.`}
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/peelingaulaser.jpg?alt=media&token=dff31f11-d7f4-4d71-bc78-f851ca3a4d78"
        }
      />

      <CardItemReverse
        id="essentiel-visage"
        price="70"
        // promoPrice="56"
        time="(45min)"
        title={
          <>
            <h2>-ESSENTIEL-</h2>
            <h2>Soin du Visage</h2>
          </>
        }
        description={
          "Le soin du visage ESSENTIEL se concentre sur le nettoyage et la purification de la peau en utilisant des dispositifs de <b>vapeur</b> et des techniques manuelles spécifiques pour l'extraction des points noirs, suivis d'un traitement stimulant au <b>courant galvanique</b>, tonifiant et améliorant la texture de la peau. Ce soin est conçu pour éliminer les impuretés, les cellules mortes, l'excès de sébum et les résidus qui s'accumulent sur la peau, la laissant propre, fraîche et hydratée."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/esential.jpg?alt=media&token=5fa5c729-8b75-4c1f-a0e8-da8350587d93"
        }
      />
      <CardItem
        id="gold-hydrafacial"
        price={"90"}
        // promoPrice="72"
        time="(50min)"
        title={
          <>
            <h2>-GOLD-</h2>
            <h2>Soin du Visage avec Hydrafacial Diamant</h2>
          </>
        }
        description={
          "Combine la phase de nettoyage du traitement ESSENTIEL suivie de l'application de principes actifs antioxydants et hydratants, et du traitement de <b>micropeeling</b> à l'aide de la tête diamantée du <b>Hydrafacial Diamant</b>."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/gold.jpg?alt=media&token=2cb517d4-31b7-497b-ab29-8ee211822dc2"
        }
      />
      <CardItemReverse
        id="diamond-hydrafacial"
        price={"110"}
        // promoPrice="88"
        time="(50min)"
        title={
          <>
            <h2>-DIAMOND-</h2>
            <h2>Soin du Visage avec Hydrafacial Diamant</h2>
          </>
        }
        description={
          "Il reprend les bienfaits des soins ESSENTIEL et GOLD en le personnalisant avec des <b>sérums</b> adaptés aux besoins spécifiques de la peau, suivis de l'application de principes actifs antioxydants et hydratants grâce à la <b>technologie ionique</b> ION (Ionization Optimized Nourishment) et à une séance de <b>photothérapie</b> LED."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/diamont.jpg?alt=media&token=7763537f-8e54-4e53-85e1-37ac7ed2deac"
        }
      />
      <CardItem
        id="platinum-hydrafacial"
        price={"130"}
        // promoPrice="104"
        time="(50min)"
        title={
          <>
            <h2>-PLATINUM-</h2>
            <h2>Soin du Visage avec Hydrafacial Diamant</h2>
          </>
        }
        description={
          "Il reprend les bienfaits des soins ESSENTIEL, GOLD et DIAMOND et offre des avantages supplémentaires grâce au traitement de <b>radiofréquence</b> à effet rajeunissant et stimulant, suivi d'un traitement de <b>drainage lymphatique</b> par ventouses. Les bienfaits du soin sont visibles dès la première séance, et les résultats continuent de s'améliorer à mesure que le collagène naturel se restaure au fil du temps."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/platinum.jpg?alt=media&token=0471be6d-f0b2-4a81-8674-7600381e6ba8"
        }
      />

      <CardItemReverse
        id="mesotherapie"
        price={"120"}
        // promoPrice="96"
        time="(50min)"
        title={"Mésothérapie"}
        description={
          "Le traitement de <b>microneedling</b> associe l'efficacité des sérums riches en peptides, des vitamines et des antioxydants essentiels pour revitaliser l'épiderme, en utilisant un système de micro-aiguilles, tel que le dermapen. Cette technique permet d'optimiser l'absorption des nutriments et de stimuler la régénération cutanée. Les avantages du microneedling incluent la réduction des <b>cicatrices</b> post-acnéiques et des <b>ridules</b>, l'élimination de l'<b>acné</b>, des <b>taches</b> pigmentaires et des <b>pores dilatés</b>, ainsi que le raffermissement et l'éclat de la peau."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/mesopothamie.jpg?alt=media&token=40b1ba7d-a268-4bde-be9b-f7d836dc9dea"
        }
      />

      <CardItem
        id="depigmentation-sourcils"
        price={"150"}
        // promoPrice="120"
        title={"Dépigmentation des sourcils et des tatouages au Pico-Laser"}
        description={
          "Pour le <b>détatouage</b> ou la <b>dépigmentation</b> des sourcils, on utilise un laser de dernière génération sans altérer la peau, en agissant sur le pigment avec la capacité de le fragmenter en microparticules, facilitant ainsi leur élimination par le système immunitaire du corps. Ce traitement est efficace pour une gamme variée de couleurs de tatouage et types de peau."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/picolaser.jpg?alt=media&token=b5ed24a6-92c4-4b8b-a749-72d340171e80"
        }
      />

      <CardItemReverse
        id="depigmentation-taches"
        price={"50 - 100"}
        // promoPrice="40 - 80"
        title={"Dépigmentation des taches avec Pico-Laser"}
        description={
          "L'élimination des taches pigmentaires au laser est une procédure cosmétique utilisée pour traiter et réduire l'apparence des taches pigmentaires sur la peau, telles que les taches de <b>vieillesse</b>, les taches de <b>rousseur</b>, le <b>mélasma</b> et les <b>taches</b> solaires. Elle consiste à utiliser un laser spécial pour cibler et détruire les cellules pigmentées excessives de la peau."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/20240620_074248.jpg?alt=media&token=c4034fc1-c40f-4807-ac8e-d33d1f83ccf7"
        }
      />

      <CardItem
        id="epilation-definitive"
        category="body-sevice"
        price={"Visage complet - 70"}
        price1={"Lévre supérieure - 35"}
        price2={"Menton - 45"}
        price3={"Aisselles - 60"}
        price4={"Maillot - 110"}
        price5={"Jambes entières - 150"}
        price6={"Corps entier - 300"}
        title={"Épilation définitive"}
        description={
          "L’épilation permanente au <b>Laser Diode ICE</b> utilise la technologie de pointe pour éliminer <b>définitivement</b> les poils indésirables en endommageant la racine du poil sans affecter les tissus environnants, empêchant ainsi leur croissance future. Cette méthode est efficace sur différents types de peau, toutes les zones du corps et en toutes saisons. Les autres avantages de l'épilation au Laser Diode ICE sont la <b>précision</b>, l'absence de douleur, la réduction ou l'élimination des poils incarnés et l'amélioration de l'apparence de la peau.<br/><br/> <span style=\"color: red;\">Important : il est nécessaire que la zone soit rasée 24 heures avant le traitement.</span> "
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/epildefinitive.jpg?alt=media&token=30c4cc72-d956-4abf-ab02-1beedabd6849"
        }
      />
      <CardItemReverse
        id="epilation-cire"
        price={"Visage complet (sourcils non inclus) - 25"}
        price1={"Sourcils (entretien + création) - 18"}
        price2={"Teinture des sourcils - 7"}
        price3={"Lèvre supérieure - 9"}
        price4={"Menton - 9"}
        price5={"Nez - 7"}
        price6={"Aisselles - 20"}
        price7={"Bras - 25"}
        price8={"Bikini complet - 30"}
        price9={"Jambes 1/2 - 25"}
        price10={"Jambes entières - 40"}
        title={"Épilation à la cire"}
        description={
          "L’épilation à la cire est une méthode populaire pour éliminer les poils indésirables de diverses zones du corps, permettant d’obtenir une peau lisse pendant une certaine période."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/epilare.jpg?alt=media&token=0d2bee27-823d-4e91-9188-71d17555abc7"
        }
      />

      <CardItem
        id="elimination-varices"
        price={"80 - 150"}
        // promoPrice="64 - 120"
        title={"Élimination au laser des varices fines et de la couperose"}
        description={
          "Le traitement est réalisé avec un <b>laser dermatologique</b> de pointe, offrant des résultats efficaces et un grand confort <b>sans douleur</b>. Les avantages incluent un temps de récupération court ou inexistant et des résultats efficaces."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/asdasd.jpg?alt=media&token=a6fcff5a-70c5-450a-a5e2-16aac93f45fe"
        }
      />

      <CardItemReverse
        id="cryotherapie"
        //  promoPrice="1ère séance - 210"
        price={`1ère séance - 300`}
        // customPrice={"custom"}
        price1={"2ème séance - 280"}
        price2={"3ème séance - 260"}
        // time="(60min)"
        title={"Cryothérapie"}
        description={`Le traitement de réduction des graisses localisées par cryothérapie agit par un processus de <b>congélation</b> de la <b>graisse</b> persistante, qui semble difficile à éliminer même avec des régimes stricts ou des exercices intenses, tels que ceux ciblant la zone des cuisses ou de la taille. <br/><br/>

La technologie non invasive de la cryolipolyse est spécialement conçue pour traiter la graisse localisée, et, associée à une alimentation équilibrée, constitue une solution efficace pour les zones problématiques. <br/> <br/>

Le processus se déroule par la cristallisation des cellules graisseuses à des températures basses sans affecter les tissus environnants, provoquant l'apoptose (mort cellulaire programmée) de celles-ci et permettant au corps de les éliminer naturellement.<br/><br/>

Le résultat est une peau tonifiée, ferme et visiblement améliorée. <br/><br/>

Après le traitement, vous pouvez reprendre vos activités quotidiennes sans restrictions.`}
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/unnamed.jpg?alt=media&token=ccb7c988-9849-49b9-b02b-89dc107cd020"
        }
      />

      <CardItem
        id="raffermissement-corporel"
        price={"180"}
        time="(45min)"
        title={"Remodelage Corporel"}
        description={
          "Dans le traitement de Remodelage Corporel par <b>Radiofréquence</b>, <b>Lipocavitation</b> et <b>Lipolaser</b> pour les problèmes de <b>flaccidité</b> de la peau et de <b>cellulite</b>, nous utilisons des dispositifs avec des longueurs d'onde spécifiques de haute intensité, pour <b>décomposer</b> les <b>cellules graisseuses</b> et <b>stimuler</b> efficacement la production de <b>collagène</b> et <b>d'élastine</b>, permettant ainsi de sculpter la <b>silhouette</b> et de raffermir la peau, sans interventions chirurgicales.<br/>\n\n" +
          "Les zones à traiter sont : la <b>culotte de cheval</b>, les <b>cuisses</b> (intérieur et extérieur), la <b>taille</b> et les <b>jambes</b>. <br/><br/>" +
          "La procédure est peu invasive et la patiente ressent souvent seulement une légère chaleur, sans inconfort significatif. <br/><br/>" +
          "Les résultats sont visibles après quelques séances et aucun temps de récupération n'est nécessaire."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/2025-07-25-00-39-48-753.jpg?alt=media&token=0ec37471-2469-4a96-8488-01f39af118e0"
        }
      />

      <CardItemReverse
        id="hifu-raffermissement"
        // price={"85"}
        // time="(60min)"
        title={"Raffermissement Corporel par HIFU"}
        description={
          "Le traitement par la technologie HIFU (High Intensity Focused Ultrasound) pour le traitement de la " +
          "<b>cellulite</b>, l'amélioration de l'<b>élasticité</b> et le <b>rajeunissement</b> de la peau représente une alternative aux interventions complexes, destinée à sculpter et à renforcer la forme du corps, en utilisant des ultrasons focalisés de haute intensité qui pénètrent les couches profondes de la peau, chauffant les tissus à des températures élevées, en détruisant le <b>tissu adipeux</b> et provoquant une réaction naturelle de <b>régénération</b>. <br/>\n\n" +
          "Les zones à traiter sont le <b>ventre</b>, les <b>cuisses</b> (intérieur et extérieur), les <b>bras</b>, les <b>poignées d'amour</b>, la <b>culotte de cheval</b>, les <b>fesses</b> et les <b>genoux</b>. <br/>\n\n" +
          "La patiente peut rapidement reprendre ses activités quotidiennes, et les résultats deviennent progressivement visibles au cours de plusieurs semaines, atteignant un maximum dans environ 3 à 6 mois. <br/>\n\n" +
          "Les effets peuvent durer entre 12 et 18 mois, en fonction de chaque individu et de son mode de vie."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/hifu-corporla-2.jpg?alt=media&token=56920c81-7b72-4149-a2b2-7ad3303aebc2"
        }
      />
      {/* 
      <CardItem
        id="massage-therapeutique"
        category="massage"
        // price={"85"}
        // time="(60min)"
        title={"Massage thérapeutique"}
        description={
          "Dans notre centre, nous vous proposons une expérience unique de détente et de bien-être grâce à nos services exclusifs de massages thérapeutiques. Conçus pour soulager le stress et la douleur, ils revitalisent votre corps et votre esprit à chaque séance."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/Massage%20the%CC%81rapeutique.jpg?alt=media&token=302d8057-ff6e-428e-a05b-f036678d9d11"
        }
      /> */}

      {/* <CardItem
        // price={"50"}
        // time="(30min)"
        title={"Réflexologie plantaire"}
        description={
          "Cette technique millénaire repose sur l'application de pressions sur des points spécifiques des pieds, correspondant à différents organes et systèmes du corps. En stimulant ces zones réflexes, nous favorisons une relaxation profonde, améliorons la circulation et renforçons la capacité naturelle du corps à guérir."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/reflex.jpg?alt=media&token=dcea2b9b-53ce-4b31-8a15-9e930d739d2c"
        }
      />
      <CardItemReverse
        // price={"85"}
        // time="(60min)"
        title={"Drainage lymphatique"}
        description={
          "Découvrez le drainage lymphatique dans notre centre de bien-être. Cette technique douce et rythmée stimule votre système lymphatique, aidant à éliminer les toxines, à réduire les gonflements et à améliorer la circulation. Parfaite pour détoxifier votre corps et renforcer votre bien-être."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/drenaje-linfatico-1024x930.jpg?alt=media&token=20cbbd35-65e4-4dd2-8bc5-5140e3ca1e44"
        }
      /> */}
      <CardItem
        id="nos-produits"
        category="products"
        title={"Nos produits"}
        description={
          "Dans notre centre esthétique, nous sélectionnons soigneusement des produits parmi les meilleures marques du marché, avec des résultats prouvés dans le domaine des soins de la peau, afin de répondre aux besoins uniques de chacune de nos clientes."
        }
        Src={
          "https://firebasestorage.googleapis.com/v0/b/esthetiquebasilix-7e531.appspot.com/o/products.jpg?alt=media&token=0d000602-8188-4186-a2b6-eaaea8cd7d9e"
        }
      />
    </section>
  );
};

// Mapping categorie → tratamente (id, titlu)
export const categoryTreatments = [
  {
    category: "face",
    label: "Visage",
    description: "Découvrez le secret d'une peau radieuse et étonnez-vous.",
    treatments: [
      { id: "lifting-hifu", title: "Lifting HIFU" },
      { id: "soin-rADiaNce", title: "RADIANCE - Traitement à l'ADN de saumon"},
      { id: "peeling-acide", title: "Peeling acide" },
      { id: "peeling-laser", title: "Peeling au Laser" },
      { id: "essentiel-visage", title: "ESSENTIEL - Soin du Visage" },
      {
        id: "gold-hydrafacial",
        title: "GOLD - Soin du Visage avec Hydrafacial Diamant",
      },
      {
        id: "diamond-hydrafacial",
        title: "DIAMOND - Soin du Visage avec Hydrafacial Diamant",
      },
      {
        id: "platinum-hydrafacial",
        title: "PLATINUM - Soin du Visage avec Hydrafacial Diamant",
      },
      { id: "mesotherapie", title: "Mésothérapie" },
      {
        id: "depigmentation-sourcils",
        title: "Dépigmentation des sourcils et des tatouages au Pico-Laser",
      },
      {
        id: "depigmentation-taches",
        title: "Dépigmentation des taches avec Pico-Laser",
      },
      {
        id: "elimination-varices",
        title: "Élimination au laser des varices fines et de la couperose",
      },
      { id: "epilation-definitive", title: "Épilation définitive" },
      { id: "epilation-cire", title: "Épilation à la cire" },
    ],
  },
  {
    category: "body-sevice",
    label: "Corps",
    description: "Nos soins pour une peau plus saine et sans poils.",
    treatments: [
      { id: "epilation-definitive", title: "Épilation définitive" },
      { id: "epilation-cire", title: "Épilation à la cire" },
      {
        id: "elimination-varices",
        title: "Élimination au laser des varices fines et de la couperose",
      },
      {
        id: "depigmentation-sourcils",
        title: "Dépigmentation des sourcils et des tatouages au Pico-Laser",
      },
      { id: "raffermissement-corporel", title: "Remodelage Corporel" },
      {
        id: "hifu-raffermissement",
        title: "Raffermissement Corporel par HIFU",
      },
    ],
  },
  {
    category: "Silhouette",
    label: "Silhouette",
    description: "Transformez votre silhouette.",
    treatments: [
      { id: "cryotherapie", title: "Cryothérapie" },
      { id: "raffermissement-corporel", title: "Remodelage Corporel" },
      {
        id: "hifu-raffermissement",
        title: "Raffermissement Corporel par HIFU",
      },
    ],
  },
  // {
  //   category: "massage",
  //   label: "Massage",
  //   description: "Renouvelez votre énergie.",
  //   treatments: [
  //     { id: "massage-therapeutique", title: "Massage thérapeutique" },
  //     // { id: "reflexologie", title: "Réflexologie plantaire" },
  //     // { id: "drainage-lymphatique", title: "Drainage lymphatique" },
  //   ],
  // },
  {
    category: "products",
    label: "Nos produits",
    description: "Voir.",
    treatments: [{ id: "nos-produits", title: "Nos produits" }],
  },
];

export default Cart;
